import {
  Component,
  Input,
  ViewEncapsulation,
  OnInit,
  EventEmitter,
  Output,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { InputCheckboxComponent } from './component/checkbox/input-checkbox.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ResetCheckStateEvent, SelectableCards, ToggleSegmentData } from './model/selectable-card.model';
import { ToggleSegmentDetail } from './model/toggle-segment-detail';

@Component({
  selector: 'vm-selectable-card',
  templateUrl: './selectable-card.component.html',
  styleUrls: ['./selectable-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SelectableCardsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() selectableCard: SelectableCards;
  @Input() isChecked: boolean;
  @Input() headingMultiLineHeight: number;
  @Input() isGroupHavingToggle: boolean;
  @Input() includeModalOkButton: boolean;
  @Input() includedTextVisibility = true;
  @Input() chargesTextVisibility = false;
  @Input() includeSecondaryCloseButton = false;
  @Input() imgSizeManipulation = false;
  @Output() addToggleOption: EventEmitter<string> = new EventEmitter(true);
  @Output() removeToggleOption: EventEmitter<string> = new EventEmitter(true);
  @Output() resetCheckedState: EventEmitter<ResetCheckStateEvent> = new EventEmitter();
  toggleSegmentData: ToggleSegmentDetail;
  selectedToggleOption: number;
  toggleAmount: number;
  toggleDiscountAmount: number;
  toggleProductId: string;
  isModalOpen: boolean;
  layer?: string;
  optionsSegment = ['SD', 'HD'];
  currentSelectedToggleOptionId: string;

  private destroyed$: Subject<boolean> = new Subject<boolean>();
  @ViewChild('checkboxElement', { static: false }) private checkboxElement: InputCheckboxComponent;

  constructor() {}

  get areImageAvailable() {
    let isAllowed = false;
    if (this.selectableCard.images !== undefined && this.selectableCard.images.length !== 0) {
      isAllowed = true;
    }
    return isAllowed;
  }

  ngOnInit(): void {
    this.initializeToggleData();
    this.setLayer();
  }

  ngAfterViewInit() {
    if (this.isCheckboxButtonAvailable && this.selectableCard.updateCheckboxState) {
      this.selectableCard.updateCheckboxState.pipe(takeUntil(this.destroyed$)).subscribe(({ productId, isChecked }) => {
        this.checkboxElement.checkboxControl.setValue(isChecked);
        // @ts-ignore
        this.resetCheckedState.emit({ radioOrCheckbox: this.selectableCard.isRadioOrCheckBox, productId, isChecked });
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getSelectedToggle(selectedOption: string) {
    this.toggleSegmentData = {
      selectedToggleOption: '',
      productId: '',
      amount: 0,
    };
    let selectedItem: ToggleSegmentData;
    if (
      this.selectableCard.toggleSegmentDetails !== undefined &&
      this.selectableCard.toggleSegmentDetails.length !== 0
    ) {
      this.selectableCard.toggleSegmentDetails
        .filter((item) => item.key === selectedOption)
        .forEach((item) => {
          selectedItem = item.value;
          this.toggleAmount = +item.value.amount;
          this.toggleDiscountAmount = item.value.discountAmount !== undefined ? +item.value.discountAmount : 0;
          this.toggleProductId = item.value.productId;
        });
      this.toggleSegmentData = {
        selectedToggleOption: selectedOption,
        productId: this.toggleProductId,
        amount: this.toggleAmount - this.toggleDiscountAmount,
        displayValue: this.selectableCard.tileHeading,
        serviceCategory: this.selectableCard.serviceCategory,
        isRadioOrCheckBox: this.selectableCard.isRadioOrCheckBox,
      };
      this.selectableCard.selectedToggleSegmentDetail = this.toggleSegmentData;
      if (
        this.selectableCard.isDefaultProduct &&
        selectedItem &&
        this.selectableCard.addOnProductId &&
        this.currentSelectedToggleOptionId !== selectedItem.productId
      ) {
        // @ts-ignore
        this.currentSelectedToggleOptionId = selectedItem.productId;
        if (selectedItem.productId === this.selectableCard.addOnProductId) {
          this.addToggleOption.emit(this.selectableCard.addOnProductId);
        } else {
          this.removeToggleOption.emit(this.selectableCard.addOnProductId);
        }
      }
    }
  }

  clickEventPropagation(event: Event) {
    event.stopPropagation();
  }

  radioClickEventPropagation(event: Event) {
    event.preventDefault();
  }

  checkboxClickEventPropagation(event: Event) {
    event.preventDefault();
  }

  private initializeToggleData() {
    this.selectableCard.modalButtonText = this.selectableCard.modalButtonText || 'Select';

    if (
      this.selectableCard.toggleSegmentDetails !== undefined &&
      this.selectableCard.toggleSegmentDetails.length !== 0
    ) {
      this.selectableCard.toggleSegmentDetails.forEach((item) => {
        if (item.value.isSelected) {
          if (item.key === 'SD') {
            this.selectedToggleOption = 0;
          } else {
            this.selectedToggleOption = 1;
          }
          this.toggleAmount = +item.value.amount;
          this.toggleDiscountAmount = item.value.discountAmount !== undefined ? +item.value.discountAmount : 0;
          this.toggleProductId = item.value.productId;
          this.currentSelectedToggleOptionId = item.value.productId;
        }
      });
    }
  }

  get isRadioButtonAvailable(): boolean {
    if (this.selectableCard.isRadioOrCheckBox === 'Radio') {
      return true;
    }
    return false;
  }

  get isCheckboxButtonAvailable(): boolean {
    if (this.selectableCard.isRadioOrCheckBox === 'Checkbox') {
      return true;
    }
    return false;
  }

  get classes() {
    let imzClass = 'tile-image__container';
    if (this.selectableCard.images?.length !== undefined) {
      if (this.selectableCard.images?.length > 2) {
        imzClass = `${imzClass}--flex`;
      } else if (this.selectableCard.images?.length === 2) {
        imzClass = `${imzClass}--double`;
      } else if (this.selectableCard.images?.length === 1 && this.selectableCard.isBorder) {
        imzClass = `${imzClass}--single-border`;
        this.layer = 'single-image-border';
      }else if (this.selectableCard.images?.length === 1 && this.imgSizeManipulation) {
        imzClass = `${imzClass}--single`;
        this.layer = 'single-image-comp';
      } else {
        imzClass = `${imzClass}--single`;
        this.layer = 'single';
      }
    }
    return imzClass;
  }

  setLayer() {
    let imzClass = 'tile-image__container';
    if (this.selectableCard.images?.length !== undefined) {
      if (this.selectableCard.images?.length > 2) {
        imzClass = `${imzClass}--flex`;
      } else if (this.selectableCard.images?.length === 2) {
        imzClass = `${imzClass}--double`;
      } else if (this.selectableCard.images?.length === 1 && this.selectableCard.isBorder) {
        imzClass = `${imzClass}--single-border`;
        this.layer = 'single-image-border';
      }else if (this.selectableCard.images?.length === 1 && this.imgSizeManipulation) {
        imzClass = `${imzClass}--single`;
        this.layer = 'single-image-comp';
      } else {
        imzClass = `${imzClass}--single`;
        this.layer = 'single';
      }
    }
  }

  /**
   * This function controls the modal button visibility
   * Rule:
   * 1) if card is already selected then dont show button and ignore includeModalOkbutton flag
   * 2) if card is not selected and includeModalOkButton flag is not provided/passed then show button
   * 3) if card is not selected and includeModalOkButton flag is provided then show/hide based on flag
   *
   * @returns boolean which controls modal dialog button
   */
  isModalButtonRequired() {
    if (this.isChecked) {
      return false;
    } else {
      if (typeof this.includeModalOkButton !== 'undefined') {
        return this.includeModalOkButton;
      } else {
        return true;
      }
    }
  }

  openModal(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    event.cancelBubble = true;
    this.isModalOpen = true;
  }

  closeOkay(event: Event) {
    this.isModalOpen = false;
    if (this.isChecked) {
      event.stopPropagation();
      event.preventDefault();
      event.cancelBubble = true;
    }
  }

  closeModal(event: Event) {
    event.preventDefault();
    this.isModalOpen = false;
    event.stopPropagation();
    event.cancelBubble = true;
  }
}
